import api from './api';
import { User } from '../types';

// Helper function to check if it's a network error
const isNetworkError = (error: any): boolean => {
  return !error.response && (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK'));
};

export const userApi = {
  // Get all users
  getUsers: async (): Promise<User[]> => {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error: any) {
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to fetch users');
    }
  },

  // Get user by ID
  getUser: async (userId: string): Promise<User> => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to fetch user');
    }
  },

  // Create user
  createUser: async (userData: any): Promise<User> => {
    try {
      const response = await api.post('/users', userData);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 422) {
        const detail = error.response.data?.detail;
        if (Array.isArray(detail)) {
          throw new Error(detail.map((err: any) => err.msg).join(', '));
        }
        throw new Error('Invalid user data');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to create user');
    }
  },

  // Update user
  updateUser: async (userId: string, userData: any): Promise<User> => {
    try {
      const response = await api.put(`/users/${userId}`, userData);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (error.response?.status === 422) {
        const detail = error.response.data?.detail;
        if (Array.isArray(detail)) {
          throw new Error(detail.map((err: any) => err.msg).join(', '));
        }
        throw new Error('Invalid user data');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to update user');
    }
  },

  // Activate user
  activateUser: async (userId: string): Promise<User> => {
    try {
      const response = await api.post(`/users/${userId}/activate`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to activate user');
    }
  },

  // Deactivate user
  deactivateUser: async (userId: string): Promise<User> => {
    try {
      const response = await api.post(`/users/${userId}/deactivate`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  },

  // Delete user
  deleteUser: async (userId: string): Promise<void> => {
    try {
      await api.delete(`/users/${userId}`);
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('User not found');
      }
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection.');
      }
      throw new Error(error.response?.data?.detail || 'Failed to delete user');
    }
  },
};
